/**
 * FocusModule - Page component for the Pomodoro/Focus module.
 *
 * The Pomodoro store is always initialized at app boot (not lazy) since
 * the timer runs across modules. This page renders the timer UI, stats,
 * and configuration. Data loading for stats happens on mount.
 *
 * @see Requirements 7.1, 7.2, 7.5, 7.6, 7.7, 13.3
 */

import { useEffect, useMemo, useState, useCallback } from 'react';
import { usePomodoroStore } from '@/stores';
import { PomodoroTimer } from '@/components/pomodoro/PomodoroTimer';
import { PomodoroStats } from '@/components/pomodoro/PomodoroStats';
import { PomodoroConfig } from '@/components/pomodoro/PomodoroConfig';
import { PomodoroNotifications } from '@/components/pomodoro/PomodoroNotifications';

export default function FocusModule() {
  const phase = usePomodoroStore((s) => s.phase);
  const loadDailyStats = usePomodoroStore((s) => s.loadDailyStats);
  const [activeTab, setActiveTab] = useState<'timer' | 'stats' | 'settings'>('timer');

  // Lazy hydration for stats: load daily stats when module mounts
  useEffect(() => {
    loadDailyStats();
  }, [loadDailyStats]);

  // Memoized: determine if timer is active for UI hints
  const isTimerActive = useMemo(
    () => phase === 'work' || phase === 'shortBreak' || phase === 'longBreak',
    [phase]
  );

  const handleTabChange = useCallback((tab: 'timer' | 'stats' | 'settings') => {
    setActiveTab(tab);
  }, []);

  return (
    <div className="relative flex flex-col h-full p-6 gap-4">
      {/* Header */}
      <h1 className="text-heading-1 font-extrabold text-white tracking-tight">Focus</h1>

      {/* Tab navigation */}
      <div className="flex items-center gap-1 glass rounded-full p-1" role="tablist">
        {(['timer', 'stats', 'settings'] as const).map((tab) => (
          <button
            key={tab}
            type="button"
            role="tab"
            aria-selected={activeTab === tab}
            onClick={() => handleTabChange(tab)}
            className={[
              'flex-1 px-4 py-2 text-sm font-medium rounded-full',
              'transition-all duration-300 ease-in-out',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus-ring',
              activeTab === tab
                ? 'bg-white/10 text-white shadow-sm'
                : 'text-text-secondary hover:text-text-primary hover:bg-white/5',
            ].join(' ')}
          >
            {tab === 'timer' ? 'Timer' : tab === 'stats' ? 'Stats' : 'Settings'}
            {tab === 'timer' && isTimerActive && (
              <span
                className="ml-1.5 w-2 h-2 rounded-full bg-purple-400 inline-block animate-pulse"
                aria-label="Timer active"
              />
            )}
          </button>
        ))}
      </div>

      {/* Tab content */}
      <div className="flex-1 min-h-0 overflow-y-auto">
        {activeTab === 'timer' && <PomodoroTimer />}
        {activeTab === 'stats' && <PomodoroStats />}
        {activeTab === 'settings' && <PomodoroConfig />}
      </div>

      {/* Notifications handler (always rendered for audio/visual alerts) */}
      <PomodoroNotifications />
    </div>
  );
}
